export type ProductDomain = "mis" | "erp" | "crm" | "aggregator" | "marketplace" | "finance";
export type ProductTargetSurface =
  | "doctor_workspace"
  | "patient_workspace"
  | "admin_workspace"
  | "crm_workspace"
  | "warehouse_workspace"
  | "finance_dashboard"
  | "public_edge"
  | "worker_internal";
export type ProductReadModelDataScope =
  | "metadata_only"
  | "reference_snapshot"
  | "public_projection"
  | "deidentified_projection"
  | "aggregate_snapshot";
export type ProductStorageContext = "system_catalog_db" | "clinic_db" | "object_storage" | "cache";
export type ProductRebuildStrategy = "event_driven" | "scheduled" | "manual_rebuild" | "cdc_projection";
export type ProductOrchestrationMode = "saga" | "state_machine" | "pipeline" | "manual_review";
export type ProductStepKind =
  | "command"
  | "projection"
  | "document"
  | "notification"
  | "human_review"
  | "external_integration"
  | "report";

export interface ProductReadModelContract {
  readModelKey: string;
  title: string;
  productDomain: ProductDomain;
  ownerServiceKey: string;
  ownerContourKey: string;
  sourceEventKey: string;
  targetSurface: ProductTargetSurface;
  dataScope: ProductReadModelDataScope;
  storageContext: ProductStorageContext;
  rebuildStrategy: ProductRebuildStrategy;
  freshnessSloSeconds: number;
  maxPayloadBytes: number;
  patientDataAllowed: boolean;
  piiAllowed: boolean;
  safetyPolicy: Record<string, unknown>;
}

export interface ProductOrchestrationContract {
  orchestrationKey: string;
  title: string;
  flowKey: string;
  productDomain: ProductDomain;
  orchestratorServiceKey: string;
  ownerContourKey: string;
  triggerEventKey: string;
  orchestrationMode: ProductOrchestrationMode;
  humanApprovalRequired: boolean;
  patientConsentRequired: boolean;
  maxRuntimeSeconds: number;
  stateStorageContext: ProductStorageContext;
  compensationPolicy: Record<string, unknown>;
  auditPolicy: Record<string, unknown>;
}

export interface ProductOrchestrationStepContract {
  stepKey: string;
  orchestrationKey: string;
  stepOrder: number;
  moduleKey: string;
  consumerServiceKey: string;
  stepKind: ProductStepKind;
  inputContract: string;
  outputEventKey?: string;
  idempotencyKeyPattern: string;
  retryPolicy: Record<string, unknown>;
  auditRequired: boolean;
}

export const productReadModelContracts: ProductReadModelContract[] = [
  {
    readModelKey: "mis.questionnaire-datapoint-snapshot",
    title: "MIS questionnaire datapoint snapshot for doctor workspace",
    productDomain: "mis",
    ownerServiceKey: "medical-core-cds",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "patient.health-questionnaire.submitted.v1",
    targetSurface: "doctor_workspace",
    dataScope: "metadata_only",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 120,
    maxPayloadBytes: 16384,
    patientDataAllowed: true,
    piiAllowed: false,
    safetyPolicy: { noRawAnswersInEvent: true, boundedRefetch: true },
  },
  {
    readModelKey: "mis.cdss-suggestion-read-model",
    title: "Doctor-facing CDSS suggestion read model",
    productDomain: "mis",
    ownerServiceKey: "clinic-api",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "medical.cdss.suggestions-ready.v1",
    targetSurface: "doctor_workspace",
    dataScope: "metadata_only",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 60,
    maxPayloadBytes: 16384,
    patientDataAllowed: true,
    piiAllowed: false,
    safetyPolicy: { humanInLoop: true, finalDiagnosisByDoctorOnly: true },
  },
  {
    readModelKey: "mis.treatment-plan-candidate-read-model",
    title: "Treatment plan candidate read model",
    productDomain: "mis",
    ownerServiceKey: "medical-core-cds",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "medical.diagnosis.confirmed.v1",
    targetSurface: "doctor_workspace",
    dataScope: "metadata_only",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 120,
    maxPayloadBytes: 32768,
    patientDataAllowed: true,
    piiAllowed: false,
    safetyPolicy: { doctorDecisionRequired: true, candidateOnly: true },
  },
  {
    readModelKey: "mis.plan-document-draft-read-model",
    title: "Plan, IDS and refusal document draft state",
    productDomain: "mis",
    ownerServiceKey: "document-signature",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "medical.treatment-plan.selected.v1",
    targetSurface: "patient_workspace",
    dataScope: "metadata_only",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 180,
    maxPayloadBytes: 32768,
    patientDataAllowed: true,
    piiAllowed: true,
    safetyPolicy: { noDocumentBodyInEvent: true, objectStorageRefsOnly: true },
  },
  {
    readModelKey: "erp.inventory-cost-projection",
    title: "Inventory cost projection for finance",
    productDomain: "erp",
    ownerServiceKey: "finance-core",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "erp.stock-movement.recorded.v1",
    targetSurface: "finance_dashboard",
    dataScope: "aggregate_snapshot",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 300,
    maxPayloadBytes: 16384,
    patientDataAllowed: false,
    piiAllowed: false,
    safetyPolicy: { aggregateOnly: true, noPatientData: true },
  },
  {
    readModelKey: "erp.honest-sign-operation-read-model",
    title: "Honest Sign operation status read model",
    productDomain: "erp",
    ownerServiceKey: "clinic-api",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "erp.honest-sign.event-accepted.v1",
    targetSurface: "admin_workspace",
    dataScope: "metadata_only",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 300,
    maxPayloadBytes: 16384,
    patientDataAllowed: false,
    piiAllowed: false,
    safetyPolicy: { activeLicenseOnly: true, noMedicalRecordAccess: true },
  },
  {
    readModelKey: "crm.patient-invite-draft-read-model",
    title: "Patient invitation draft from qualified lead",
    productDomain: "crm",
    ownerServiceKey: "clinic-api",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "crm.lead-qualified.v1",
    targetSurface: "crm_workspace",
    dataScope: "metadata_only",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 120,
    maxPayloadBytes: 8192,
    patientDataAllowed: true,
    piiAllowed: true,
    safetyPolicy: { marketingConsentChecked: true, noMedicalDecisionAutomation: true },
  },
  {
    readModelKey: "crm.lead-patient-conversion-state",
    title: "CRM checked-in lead to patient conversion state",
    productDomain: "crm",
    ownerServiceKey: "crm-automation",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "schedule.appointment.checked-in.v1",
    targetSurface: "crm_workspace",
    dataScope: "metadata_only",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 60,
    maxPayloadBytes: 16384,
    patientDataAllowed: true,
    piiAllowed: true,
    safetyPolicy: {
      boundedIdentityRefetch: true,
      noMedicalRecord: true,
      noAutomaticPatientMerge: true,
      attributionSnapshotRequired: true,
    },
  },
  {
    readModelKey: "aggregator.public-booking-handoff",
    title: "Secure public booking handoff into clinic flow",
    productDomain: "aggregator",
    ownerServiceKey: "clinic-api",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "aggregator.booking-request.created.v1",
    targetSurface: "admin_workspace",
    dataScope: "metadata_only",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 60,
    maxPayloadBytes: 8192,
    patientDataAllowed: false,
    piiAllowed: false,
    safetyPolicy: { secureHandoffOnly: true, noRawPhoneOrMedicalData: true },
  },
  {
    readModelKey: "marketplace.order-cashflow-projection",
    title: "Marketplace order cashflow projection",
    productDomain: "marketplace",
    ownerServiceKey: "finance-core",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "marketplace.order.created.v1",
    targetSurface: "finance_dashboard",
    dataScope: "aggregate_snapshot",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 600,
    maxPayloadBytes: 16384,
    patientDataAllowed: false,
    piiAllowed: false,
    safetyPolicy: { commercialOnly: true, noRawPatientData: true },
  },
  {
    readModelKey: "finance.dashboard-snapshot-read-model",
    title: "Approved finance dashboard snapshot",
    productDomain: "finance",
    ownerServiceKey: "clinic-api",
    ownerContourKey: "ru-regulated-data-plane",
    sourceEventKey: "finance.report-snapshot.created.v1",
    targetSurface: "admin_workspace",
    dataScope: "aggregate_snapshot",
    storageContext: "clinic_db",
    rebuildStrategy: "event_driven",
    freshnessSloSeconds: 600,
    maxPayloadBytes: 32768,
    patientDataAllowed: false,
    piiAllowed: false,
    safetyPolicy: { approvedSnapshotRequired: true, rawMedicalDataAllowed: false },
  },
];

export const productOrchestrationContracts: ProductOrchestrationContract[] = [
  {
    orchestrationKey: "mis.patient-intake-cdss-plan",
    title: "MIS patient intake to CDSS and treatment plan candidates",
    flowKey: "mis.patient-intake-to-treatment-plan",
    productDomain: "mis",
    orchestratorServiceKey: "clinic-api",
    ownerContourKey: "ru-regulated-data-plane",
    triggerEventKey: "patient.health-questionnaire.submitted.v1",
    orchestrationMode: "state_machine",
    humanApprovalRequired: true,
    patientConsentRequired: true,
    maxRuntimeSeconds: 604800,
    stateStorageContext: "clinic_db",
    compensationPolicy: { rollback: "mark_projection_stale", manualReviewRequired: true },
    auditPolicy: { doctorDecisionRequired: true },
  },
  {
    orchestrationKey: "mis.plan-sign-schedule",
    title: "MIS selected treatment plan to documents and scheduling",
    flowKey: "mis.plan-sign-schedule",
    productDomain: "mis",
    orchestratorServiceKey: "clinic-api",
    ownerContourKey: "ru-regulated-data-plane",
    triggerEventKey: "medical.treatment-plan.selected.v1",
    orchestrationMode: "saga",
    humanApprovalRequired: false,
    patientConsentRequired: true,
    maxRuntimeSeconds: 2592000,
    stateStorageContext: "clinic_db",
    compensationPolicy: { cancelDocumentsBeforeSigning: true, releaseSlotsOnCancel: true },
    auditPolicy: { esignAudit: true },
  },
  {
    orchestrationKey: "erp.resource-lifecycle",
    title: "ERP stock, Honest Sign and resource lifecycle",
    flowKey: "erp.resource-lifecycle",
    productDomain: "erp",
    orchestratorServiceKey: "erp-warehouse",
    ownerContourKey: "ru-regulated-data-plane",
    triggerEventKey: "erp.stock-movement.recorded.v1",
    orchestrationMode: "pipeline",
    humanApprovalRequired: false,
    patientConsentRequired: false,
    maxRuntimeSeconds: 604800,
    stateStorageContext: "clinic_db",
    compensationPolicy: { reverseStockMovementRequired: true, honestSignCorrectionRequiredWhenApplicable: true },
    auditPolicy: { noPatientData: true },
  },
  {
    orchestrationKey: "crm.lead-to-patient",
    title: "CRM checked-in lead to patient conversion flow",
    flowKey: "crm.lead-to-patient",
    productDomain: "crm",
    orchestratorServiceKey: "crm-automation",
    ownerContourKey: "ru-regulated-data-plane",
    triggerEventKey: "schedule.appointment.checked-in.v1",
    orchestrationMode: "saga",
    humanApprovalRequired: true,
    patientConsentRequired: true,
    maxRuntimeSeconds: 604800,
    stateStorageContext: "clinic_db",
    compensationPolicy: {
      keepLeadHistory: true,
      doNotMergePatients: true,
      markConversionForReview: true,
      doNotDeleteCreatedPatient: true,
    },
    auditPolicy: {
      ambiguousIdentityRequiresHumanReview: true,
      attributionSnapshotRequired: true,
    },
  },
  {
    orchestrationKey: "aggregator.public-booking",
    title: "Aggregator public booking handoff to clinic schedule flow",
    flowKey: "aggregator.public-booking",
    productDomain: "aggregator",
    orchestratorServiceKey: "clinic-api",
    ownerContourKey: "ru-regulated-data-plane",
    triggerEventKey: "aggregator.booking-request.created.v1",
    orchestrationMode: "saga",
    humanApprovalRequired: false,
    patientConsentRequired: true,
    maxRuntimeSeconds: 86400,
    stateStorageContext: "clinic_db",
    compensationPolicy: { expireSecureHandoff: true, doNotCreatePatientWithoutConsent: true },
    auditPolicy: { secureHandoffOnly: true },
  },
  {
    orchestrationKey: "marketplace.order-to-service",
    title: "Marketplace order to receiving, settlement and service request",
    flowKey: "marketplace.order-to-service",
    productDomain: "marketplace",
    orchestratorServiceKey: "marketplace-commerce",
    ownerContourKey: "panama-global-control-plane",
    triggerEventKey: "marketplace.order.created.v1",
    orchestrationMode: "saga",
    humanApprovalRequired: false,
    patientConsentRequired: false,
    maxRuntimeSeconds: 2592000,
    stateStorageContext: "system_catalog_db",
    compensationPolicy: { commercialCancellation: true, partnerCreditRollback: true },
    auditPolicy: { noPatientData: true },
  },
  {
    orchestrationKey: "finance.plan-fact-loop",
    title: "Finance approved snapshot to dashboard and accounting loop",
    flowKey: "finance.plan-fact-loop",
    productDomain: "finance",
    orchestratorServiceKey: "finance-core",
    ownerContourKey: "ru-regulated-data-plane",
    triggerEventKey: "finance.report-snapshot.created.v1",
    orchestrationMode: "pipeline",
    humanApprovalRequired: true,
    patientConsentRequired: false,
    maxRuntimeSeconds: 604800,
    stateStorageContext: "clinic_db",
    compensationPolicy: { supersedeSnapshot: true, keepPreviousApprovedVersion: true },
    auditPolicy: { approvedSnapshotRequired: true },
  },
];

export const productOrchestrationSteps: ProductOrchestrationStepContract[] = [
  { stepKey: "mis.intake.build-datapoint-snapshot", orchestrationKey: "mis.patient-intake-cdss-plan", stepOrder: 10, moduleKey: "reference.mis-state-datapoints", consumerServiceKey: "medical-core-cds", stepKind: "projection", inputContract: "patient.health-questionnaire.submitted.v1 metadata refetch", idempotencyKeyPattern: "clinicId:patientId:questionnaireId:version", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "mis.intake.refresh-cdss-read-model", orchestrationKey: "mis.patient-intake-cdss-plan", stepOrder: 20, moduleKey: "reference.mis-cdss-mind-logic", consumerServiceKey: "clinic-api", stepKind: "projection", inputContract: "medical.cdss.suggestions-ready.v1 metadata refetch", outputEventKey: "medical.cdss.suggestions-ready.v1", idempotencyKeyPattern: "clinicId:patientId:visitId:suggestionSetId", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "mis.intake.doctor-diagnosis-review", orchestrationKey: "mis.patient-intake-cdss-plan", stepOrder: 30, moduleKey: "reference.mis-cdss-mind-logic", consumerServiceKey: "clinic-api", stepKind: "human_review", inputContract: "doctor confirms or changes diagnosis", outputEventKey: "medical.diagnosis.confirmed.v1", idempotencyKeyPattern: "clinicId:patientId:visitId:diagnosisDecisionId", retryPolicy: { maxAttempts: 1, backoff: "none" }, auditRequired: true },
  { stepKey: "mis.intake.rebuild-plan-candidates", orchestrationKey: "mis.patient-intake-cdss-plan", stepOrder: 40, moduleKey: "reference.mis-treatment-plan-mindmap", consumerServiceKey: "medical-core-cds", stepKind: "projection", inputContract: "medical.diagnosis.confirmed.v1 metadata refetch", idempotencyKeyPattern: "clinicId:patientId:visitId:diagnosisDecisionId:planCandidates", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "mis.plan.generate-documents", orchestrationKey: "mis.plan-sign-schedule", stepOrder: 10, moduleKey: "reference.mis-document-signing", consumerServiceKey: "document-signature", stepKind: "document", inputContract: "medical.treatment-plan.selected.v1 metadata refetch", idempotencyKeyPattern: "clinicId:patientId:planId:documentPackId", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "mis.plan.create-appointment-projection", orchestrationKey: "mis.plan-sign-schedule", stepOrder: 20, moduleKey: "reference.mis-visit-scheduling", consumerServiceKey: "clinic-api", stepKind: "projection", inputContract: "selected branch service/cabinet/duration snapshot", idempotencyKeyPattern: "clinicId:patientId:planId:selectedBranchId:scheduleProjection", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "erp.resource.refresh-inventory-cost", orchestrationKey: "erp.resource-lifecycle", stepOrder: 10, moduleKey: "reference.erp-warehouse-inventory", consumerServiceKey: "finance-core", stepKind: "projection", inputContract: "erp.stock-movement.recorded.v1 metadata refetch", idempotencyKeyPattern: "clinicId:warehouseId:stockMovementId:costProjection", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "erp.resource.sync-honest-sign-read-model", orchestrationKey: "erp.resource-lifecycle", stepOrder: 20, moduleKey: "reference.erp-honest-sign", consumerServiceKey: "clinic-api", stepKind: "projection", inputContract: "erp.honest-sign.event-accepted.v1 metadata refetch", idempotencyKeyPattern: "clinicId:honestSignOperationId:readModel", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "crm.conversion.resolve-contact-identity", orchestrationKey: "crm.lead-to-patient", stepOrder: 10, moduleKey: "reference.crm-leads-omnichannel", consumerServiceKey: "crm-automation", stepKind: "command", inputContract: "schedule.appointment.checked-in.v1 metadata and bounded patient identity refetch", outputEventKey: "crm.contact-resolved.v1", idempotencyKeyPattern: "clinicId:appointmentId:leadId:conversionVersion:identityResolution", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "crm.conversion.review-ambiguous-identity", orchestrationKey: "crm.lead-to-patient", stepOrder: 20, moduleKey: "reference.crm-leads-omnichannel", consumerServiceKey: "crm-automation", stepKind: "human_review", inputContract: "identity candidate review only for ambiguous or conflicting matches", idempotencyKeyPattern: "clinicId:appointmentId:leadId:conversionVersion:identityReview", retryPolicy: { maxAttempts: 1, backoff: "none" }, auditRequired: true },
  { stepKey: "crm.conversion.link-or-create-patient", orchestrationKey: "crm.lead-to-patient", stepOrder: 30, moduleKey: "patient.standard-flow", consumerServiceKey: "clinic-api", stepKind: "command", inputContract: "verified CRM contact link or patient creation after identity and consent gates", idempotencyKeyPattern: "clinicId:appointmentId:leadId:conversionVersion:patientLink", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "crm.conversion.close-lead-create-case", orchestrationKey: "crm.lead-to-patient", stepOrder: 40, moduleKey: "reference.crm-funnels-automation", consumerServiceKey: "crm-automation", stepKind: "command", inputContract: "patient link, immutable attribution snapshot, converted lead and patient case", outputEventKey: "crm.lead-converted-to-patient.v1", idempotencyKeyPattern: "clinicId:appointmentId:leadId:conversionVersion:leadConverted", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "aggregator.booking.create-clinic-request", orchestrationKey: "aggregator.public-booking", stepOrder: 10, moduleKey: "system.aggregator-clinic-doctor-search", consumerServiceKey: "clinic-api", stepKind: "command", inputContract: "aggregator.booking-request.created.v1 secure handoff", idempotencyKeyPattern: "clinicId:bookingRequestId:publicFlowVersion", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "aggregator.booking.patient-consent-gate", orchestrationKey: "aggregator.public-booking", stepOrder: 20, moduleKey: "patient.standard-flow", consumerServiceKey: "clinic-api", stepKind: "human_review", inputContract: "patient consent before patient profile creation", idempotencyKeyPattern: "clinicId:bookingRequestId:patientConsent", retryPolicy: { maxAttempts: 1, backoff: "none" }, auditRequired: true },
  { stepKey: "marketplace.order.refresh-cashflow", orchestrationKey: "marketplace.order-to-service", stepOrder: 10, moduleKey: "system.marketplace-settlements-logistics", consumerServiceKey: "finance-core", stepKind: "projection", inputContract: "marketplace.order.created.v1 commercial metadata refetch", idempotencyKeyPattern: "commercialAccountId:orderId:cashflowProjection", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "marketplace.order.prepare-receiving-draft", orchestrationKey: "marketplace.order-to-service", stepOrder: 20, moduleKey: "reference.erp-warehouse-inventory", consumerServiceKey: "erp-warehouse", stepKind: "command", inputContract: "marketplace order lines mapped to clinic nomenclature", idempotencyKeyPattern: "commercialAccountId:orderId:receivingDraft", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "finance.snapshot.publish-dashboard", orchestrationKey: "finance.plan-fact-loop", stepOrder: 10, moduleKey: "reference.finance-opiu-odds-planning", consumerServiceKey: "clinic-api", stepKind: "report", inputContract: "finance.report-snapshot.created.v1 approved snapshot metadata", idempotencyKeyPattern: "clinicId:reportKind:reportPeriod:snapshotVersion:dashboard", retryPolicy: { maxAttempts: 10, backoff: "exponential" }, auditRequired: true },
  { stepKey: "finance.snapshot.accounting-review", orchestrationKey: "finance.plan-fact-loop", stepOrder: 20, moduleKey: "reference.finance-accounting-management", consumerServiceKey: "finance-core", stepKind: "human_review", inputContract: "approved finance snapshot to accounting entries", idempotencyKeyPattern: "clinicId:reportKind:reportPeriod:snapshotVersion:accountingReview", retryPolicy: { maxAttempts: 1, backoff: "none" }, auditRequired: true },
];

export function productReadModelByKey(readModelKey: string): ProductReadModelContract | undefined {
  return productReadModelContracts.find((contract) => contract.readModelKey === readModelKey);
}

export function productOrchestrationByKey(orchestrationKey: string): ProductOrchestrationContract | undefined {
  return productOrchestrationContracts.find((contract) => contract.orchestrationKey === orchestrationKey);
}

export function productStepsForOrchestration(orchestrationKey: string): ProductOrchestrationStepContract[] {
  return productOrchestrationSteps
    .filter((step) => step.orchestrationKey === orchestrationKey)
    .sort((left, right) => left.stepOrder - right.stepOrder);
}

export function unsafeProductReadModels(): ProductReadModelContract[] {
  return productReadModelContracts.filter((contract) => {
    if (contract.piiAllowed && !contract.patientDataAllowed) return true;
    if (contract.patientDataAllowed && contract.ownerContourKey !== "ru-regulated-data-plane") return true;
    if (contract.patientDataAllowed && contract.dataScope !== "metadata_only") return true;
    return false;
  });
}

export function orchestrationRequiresHumanGate(orchestrationKey: string): boolean {
  return productOrchestrationByKey(orchestrationKey)?.humanApprovalRequired === true
    || productStepsForOrchestration(orchestrationKey).some((step) => step.stepKind === "human_review");
}
